import { List } from "immutable";
import PropTypes from "prop-types";
import React from "react";

import ToolbarPopoverButton from "../../components/toolbar/ToolbarPopoverButton";
import ToolbarPopoverList from "../../components/toolbar/ToolbarPopoverList";
import ToolbarViewSelector from "../../components/toolbar/ToolbarViewSelector";
import CheckboxStates from "../../constants/checkboxStates";
import {
  MINDMAP_VIEW,
  NARRATIVE_VIEW,
  OUTLINE_VIEW,
  RELATIONSHIP_VIEW,
  SPREADSHEET_VIEW,
  SUBWAY_VIEW,
  TIMELINE_VIEW
} from "../../constants/identifiers";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import { appStateOperations } from "../../modules/appState";
import { mindmapOperations } from "../../modules/mindmaps";
import { immutableRecords, PropTypeShapes } from "../../types";
import { showOnExtraConditionForReadonly } from "../../utils/codeSplit";
import preprocessorQuery from "../../utils/preprocessorQuery";

export const CREATE_MINDMAP_ID = "create_new_mindmap";

// const mobileLandscapeViewOptions = [TIMELINE_VIEW, SUBWAY_VIEW, NARRATIVE_VIEW];

const dateOrderViewOptions = immutableRecords.PopoverMenuGroupRecord({
  label: "Date Order",
  children: List([
    immutableRecords.PopoverMenuItemRecord({
      id: TIMELINE_VIEW,
      icon: "timelineView",
      color: "iconChronological",
      label: "Timeline View",
      shortcut: "⌘1"
    }),
    //#!if mobile !== true
    immutableRecords.PopoverMenuItemRecord({
      id: SPREADSHEET_VIEW,
      icon: "spreadsheetView",
      color: "iconChronological",
      label: "Spreadsheet View",
      shortcut: "⌘2"
    }),
    immutableRecords.PopoverMenuItemRecord({
      id: RELATIONSHIP_VIEW,
      icon: "relationshipView",
      color: "iconChronological",
      label: "Relationship View",
      shortcut: "⌘3"
    }),
    immutableRecords.PopoverMenuItemRecord({
      id: SUBWAY_VIEW,
      icon: "subwayView",
      color: "iconChronological",
      label: "Subway View",
      shortcut: "⌘4"
    })
    //#!endif
  ])
});

const narrativeOrderViewOptions = immutableRecords.PopoverMenuGroupRecord({
  label: "Narrative Order",
  children: List([
    immutableRecords.PopoverMenuItemRecord({
      id: NARRATIVE_VIEW,
      icon: "narrativeView",
      color: "iconNarrative",
      label: "Narrative View",
      shortcut: "⌘5"
    }),
    //#!if mobile !== true
    immutableRecords.PopoverMenuItemRecord({
      id: OUTLINE_VIEW,
      icon: "outlineView",
      color: "iconNarrative",
      label: "Outline View",
      shortcut: "⌘6"
    })
    //#!endif
  ])
});

const mindmapViewOptions = (existingMindmaps, activeMindmapId) => {
  return showOnExtraConditionForReadonly(
    immutableRecords.PopoverMenuGroupRecord({
      label: "Mindmaps",
      children: existingMindmaps
        .map(mm =>
          immutableRecords
            .PopoverMenuItemRecord(mm)
            .set("icon", "mindmapView")
            .set("state", mm.id === activeMindmapId ? CheckboxStates.CHECKED : CheckboxStates.UNCHECKED)
        )
        //#!if readonly !== true
// .push(
// immutableRecords.PopoverMenuItemRecord({
// id: CREATE_MINDMAP_ID,
// icon: "mindmapView",
// label: "Create New"
// })
// )
      //#!endif
    }),
    existingMindmaps.size > 0
  );
};

const viewPopoverOptions = (activeViewType, existingMindmaps, activeMindmapId, includeNarrative, hasNarrativeItem) => {
  const selectedDateOpts = dateOrderViewOptions.update("children", children =>
    children.map(c => (c.id === activeViewType ? c.set("state", CheckboxStates.CHECKED) : c))
  );

  const selectedNarrativeOpts =
    includeNarrative && (!preprocessorQuery.isReadonly() || hasNarrativeItem)
      ? narrativeOrderViewOptions.update("children", children =>
        children.map(c => (c.id === activeViewType ? c.set("state", CheckboxStates.CHECKED) : c))
      )
      : null;

  let mindmapOpts = null;
  //#!if mobile !== true
  mindmapOpts = mindmapViewOptions(existingMindmaps, activeMindmapId);
  //#!endif

  return List([selectedDateOpts, selectedNarrativeOpts, mindmapOpts]).filter(a => a);
};

const iconAndLabelForViewType = viewType => {
  const dateOpt = dateOrderViewOptions.children.find(c => c.id === viewType);
  if (dateOpt) {
    return { label: dateOpt.label, icon: dateOpt.icon, color: dateOpt.color };
  }
  const narrativeOpt = narrativeOrderViewOptions.children.find(c => c.id === viewType);
  if (narrativeOpt) {
    return { label: narrativeOpt.label, icon: narrativeOpt.icon, color: narrativeOpt.color };
  }

  return { label: "Mindmap View", icon: "mindmapView" };
};

const shouldPulse = pulsateIconId =>
  pulsateIconId === interfaceIdentifiers.SPREADSHEET ||
  pulsateIconId === interfaceIdentifiers.TIMELINE ||
  pulsateIconId === interfaceIdentifiers.NARRATIVE ||
  pulsateIconId === interfaceIdentifiers.SUBWAY ||
  pulsateIconId === interfaceIdentifiers.RELATIONSHIP ||
  pulsateIconId === interfaceIdentifiers.OUTLINE ||
  pulsateIconId === interfaceIdentifiers.MINDMAP;

const ActiveViewSelector = props => {
  const viewOptions = viewPopoverOptions(
    props.activeViewType,
    props.existingMindmaps,
    props.activeViewMindmapId,
    props.includeNarrative,
    props.hasNarrativeItem
  );
  const activeView = iconAndLabelForViewType(props.activeViewType);

  const onClick = id => {
    if (
      dateOrderViewOptions.children.find(opt => opt.id === id) ||
      narrativeOrderViewOptions.children.find(opt => opt.id === id)
    ) {
      props.onViewTypeClick(appStateOperations.switchViewType(id, null));
    } else if (id === CREATE_MINDMAP_ID) {
      props.onViewTypeClick(mindmapOperations.createMindmap());
    } else {
      props.onViewTypeClick(appStateOperations.switchViewType(MINDMAP_VIEW, id));
    }
  };

  return (
    <ToolbarPopoverButton placement="bottom" disabled={props.disabled}>
      <ToolbarViewSelector
        id="toolbarViewTypeButton"
        text={activeView.label}
        icon={activeView.icon}
        iconColor={activeView.color}
        iconClass="toolbarViewOptions"
        pulse={shouldPulse(props.pulsateIconId)}
        disabled={props.disabled}
      />
      <ToolbarPopoverList
        iconClass="toolbarViewOptions"
        groups={viewOptions}
        showIcons={true}
        showBorders={true}
        showSelectedIcon={true}
        //#!if iOS !== true
        showShortcuts={true}
        //#!endif
        onClick={onClick}
      />
    </ToolbarPopoverButton>
  );
};

ActiveViewSelector.propTypes = {
  disabled: PropTypes.bool,
  includeNarrative: PropTypes.bool.isRequired,
  activeViewType: PropTypes.string.isRequired,
  activeViewMindmapId: PropTypes.string,
  onViewTypeClick: PropTypes.func.isRequired,
  //#!if mobile !== true
  existingMindmaps: PropTypeShapes.mindmapList.isRequired,
  //#!endif
  pulsateIconId: PropTypes.string,
  hasNarrativeItem: PropTypes.bool
};

export default ActiveViewSelector;
